import {AfterViewInit, Component, Inject} from '@angular/core';
import {
  MAT_DIALOG_DATA, MatDialogActions,
  MatDialogContent,
  MatDialogRef, MatDialogTitle
} from '@angular/material/dialog';
import {AdministratorApiModel} from '../../../../model/administrator-api.model';
import {MatError, MatFormField, MatLabel} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatInput} from '@angular/material/input';
import {MatButton} from '@angular/material/button';
import {MatSlideToggle} from '@angular/material/slide-toggle';
import {MatTooltip} from '@angular/material/tooltip';
import {NgIf} from '@angular/common';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {UtilityService} from '../../../../service/utility.service';
import {passwordsMatchValidator} from '../../../../validator/passwords-match.validator';
import {AdminService} from '../../../../service/admin.service';
import {MatSnackBar} from '@angular/material/snack-bar';

/**
 * @class
 * @description This class maintains the confirmation dialog for the application.
 * @export
 * @name ConfirmationDialogComponent
 */
@Component({
  selector: 'app-dialog-admin-settings',
  styleUrl: './settings.component.scss',
  templateUrl: './settings.component.html',
  imports: [
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    MatSlideToggle,
    MatTooltip,
    NgIf,
    ReactiveFormsModule,
    MatDialogActions,
    MatButton
  ],
  standalone: true
})
export class SettingsAdminDialogComponent implements AfterViewInit {

  /**
   * @description The administrator object to modify.
   * @memberof SettingsAdminDialogComponent
   * @name SettingsAdminDialogComponent#administrator
   * @property
   * @protected
   * @readonly
   * @type {AdministratorApiModel}
   */
  protected readonly administrator: AdministratorApiModel;

  /**
   * @description This flag indicates if the toggles should be disabled for the administrator settings or not.
   * @memberof SettingsAdminDialogComponent
   * @name SettingsAdminDialogComponent#disableToggles
   * @property
   * @protected
   * @readonly
   * @type {boolean}
   */
  protected readonly disableToggles: boolean;

  /**
   * @description This property contains the form group for the administrator settings.
   * @memberof SettingsAdminDialogComponent
   * @name SettingsAdminDialogComponent#form
   * @property
   * @protected
   * @type {FormGroup<{
   *     authentication: FormGroup<{
   *       emailAddress: FormControl<string>,
   *       password: FormControl<string | null>,
   *       passwordConfirmation: FormControl<string | null>,
   *     }>,
   *     contact: FormGroup<{
   *       firstName: FormControl<string>,
   *       lastName: FormControl<string>
   *     }>,
   *     options: FormGroup<{
   *       created: FormControl<Date>,
   *       locked: FormControl<boolean>,
   *       passwordExpires: FormControl<boolean>,
   *       passwordLastChanged: FormControl<Date | null>,
   *       passwordPromptForChange: FormControl<boolean>,
   *       updated: FormControl<Date>
   *     }>
   *   }>}
   */
  protected readonly form: FormGroup<{
    authentication: FormGroup<{
      emailAddress: FormControl<string>,
      password: FormControl<string | null>,
      passwordConfirmation: FormControl<string | null>,
    }>,
    contact: FormGroup<{
      firstName: FormControl<string>,
      lastName: FormControl<string>
    }>,
    options: FormGroup<{
      created: FormControl<Date>,
      locked: FormControl<boolean>,
      passwordExpires: FormControl<boolean>,
      passwordLastChanged: FormControl<Date | null>,
      passwordPromptForChange: FormControl<boolean>,
      updated: FormControl<Date>
    }>
  }>;

  /**
   * @default true
   * @description This property contains the form disabled status.
   * @memberof SettingsAdminDialogComponent
   * @name SettingsAdminDialogComponent#formDisabled
   * @property
   * @protected
   * @type {boolean}
   */
  protected formDisabled: boolean = true;

  /**
   * @description This property determines whether the administrator is the current user or not.
   * @memberof SettingsAdminDialogComponent
   * @name SettingsAdminDialogComponent#isMe
   * @property
   * @protected
   * @readonly
   */
  protected readonly isMe: boolean;

  /**
   * @description This property contains the new status of the administrator.
   * @memberof SettingsAdminDialogComponent
   * @name SettingsAdminDialogComponent#isNew
   * @property
   * @protected
   * @readonly
   */
  protected readonly isNew: boolean;

  /**
   * @description This property contains the original password of the administrator.
   * @memberof SettingsAdminDialogComponent
   * @name SettingsAdminDialogComponent#originalPassword
   * @private
   * @property
   * @type {string?}
   */
  private originalPassword: string | undefined = undefined;

  /**
   * @description This property contains the password required status.
   * @memberof SettingsAdminDialogComponent
   * @name SettingsAdminDialogComponent#passwordRequired
   * @property
   * @protected
   * @type {boolean}
   */
  protected passwordRequired: boolean = false;

  /**
   * @constructor
   * @description Constructor for the SettingsAdminDialogComponent.
   * @memberof SettingsAdminDialogComponent
   * @name SettingsAdminDialogComponent#constructor()
   * @param {FormBuilder} formBuilder The form builder for the dialog.
   * @param {MatDialogRef<SettingsAdminDialogComponent>} reference The reference to the dialog.
   * @param {AdminService} service The admin service for the dialog.
   * @param {MatSnackBar} snackbar The SnackBar service for the dialog.
   * @param {{ administrator?: AdministratorApiModel, passwordRequired?: boolean }} data The data for the dialog.
   * @public
   */
  constructor(
    /**
     * @description This property contains the form builder.
     * @memberof SettingsAdminDialogComponent
     * @name SettingsAdminDialogComponent#formBuilder
     * @property
     * @protected
     * @readonly
     * @type {FormBuilder}
     */
    private readonly formBuilder: FormBuilder,
    /**
     * @description The reference to the dialog.
     * @memberof SettingsAdminDialogComponent
     * @name SettingsAdminDialogComponent#reference
     * @private
     * @property
     * @readonly
     * @type {MatDialogRef<SettingsAdminDialogComponent>}
     */
    private readonly reference: MatDialogRef<SettingsAdminDialogComponent>,
    /**
     * @description This property contains the admin service.
     * @memberof SettingsAdminDialogComponent
     * @name SettingsAdminDialogComponent#service
     * @private
     * @property
     * @readonly
     * @type {AdminService}
     */
    private readonly service: AdminService,
    /**
     * @description This property contains our SnackBar service.
     * @memberof SettingsAdminDialogComponent
     * @name SettingsAdminDialogComponent#snackbar
     * @private
     * @property
     * @readonly
     * @type {MatSnackBar}
     */
    private readonly snackbar: MatSnackBar,
    /**
     * @description The data for the dialog.
     * @memberof ConfirmationDialogComponent
     * @name ConfirmationDialogComponent#data
     * @property
     * @public
     * @type {{ administrator?: AdministratorApiModel, disableToggles?: boolean, isNew?: boolean, isMe?: boolean, passwordRequired?: boolean }}
     */
    @Inject(MAT_DIALOG_DATA) public data: { administrator?: AdministratorApiModel, disableToggles?: boolean, isNew?: boolean, isMe?: boolean, passwordRequired?: boolean }
  ) {

    // Ensure we have an administrator object.
    this.administrator = this.data.administrator ?? new AdministratorApiModel();

    // Ensure we have a disable toggles flag.
    this.disableToggles = this.data.disableToggles ?? false;

    // Determine if the administrator is me [the current user].
    this.isMe = this.data.isMe ?? false;

    // Determine if the administrator is new.
    this.isNew = this.data.isNew ?? false;

    // Ensure we have a password required flag.
    this.passwordRequired = this.data.passwordRequired ?? false;

    // Build our form and set it into the instance.
    this.form = this.formBuilder.group({

      // Define our authentication details form group.
      authentication: new FormGroup({

        // Set the email address form control into the form group in the instance.
        emailAddress: new FormControl<string>(UtilityService.EmptyString, {

          // Set the form control as non-nullable.
          nonNullable: true,

          // Set the form control as required and validate the email address.
          validators: [Validators.required, Validators.email]
        }),

        // Set the password form control into the form group in the instance.
        password: new FormControl<string | null>(this.passwordRequired ? null : UtilityService.EmptyString, {

          // Set the non-nullable flag based on the password required flag.
          nonNullable: this.passwordRequired,

          // Set the form control validators based on the password required flag.
          validators: this.passwordRequired ? [Validators.required] : undefined
        }),

        // Set the password confirmation form control into the form group in the instance.
        passwordConfirmation: new FormControl<string | null>(this.passwordRequired ? null : UtilityService.EmptyString, {

          // Set the non-nullable flag based on the password required flag.
          nonNullable: this.passwordRequired,

          // Set the form control validators based on the password required flag.
          validators: this.passwordRequired ? [Validators.required] : undefined
        })
      }, {

        // Pass our validator to the form group.
        validators: [passwordsMatchValidator('password', 'passwordConfirmation')]
      }),

      // Define our contact information form group.
      contact: new FormGroup({

        // Set the first name form control into the form group in the instance.
        firstName: new FormControl<string>(UtilityService.EmptyString, {

          // Set the form control as non-nullable.
          nonNullable: true,

          // Set the form control as required.
          validators: [Validators.required]
        }),

        // Set the last name form control into the form group in the instance.
        lastName: new FormControl<string>(UtilityService.EmptyString, {

          // Set the form control as non-nullable.
          nonNullable: true,

          // Set the form control as required.
          validators: [Validators.required]
        })
      }),

      // Define our options form group.
      options: new FormGroup({

        // Set the created form control into the form group in the instance.
        created: new FormControl<Date>(UtilityService.DateTimeUtc(), {

          // Set the form control as non-nullable.
          nonNullable: true
        }),

        // Set the locked form control into the form group in the instance.
        locked: new FormControl<boolean>(false, {

          // Set the form control as non-nullable.
          nonNullable: true,

          // Set the form control as required.
          validators: [Validators.required]
        }),

        // Set the password expires form control into the form group in the instance.
        passwordExpires: new FormControl<boolean>(false, {

          // Set the form control as non-nullable.
          nonNullable: true,

          // Set the form control as required.
          validators: [Validators.required]
        }),

        // Set the password last changed form control into the form group in the instance.
        passwordLastChanged: new FormControl<Date | null>(this.passwordRequired ? UtilityService.DateTimeUtc() : null),

        // Set the password prompt for change form control into the form group in the instance.
        passwordPromptForChange: new FormControl<boolean>(false, {

          // Set the form control as non-nullable.
          nonNullable: true,

          // Set the form control as required.
          validators: [Validators.required]
        }),

        // Set the updated form control into the form group in the instance.
        updated: new FormControl<Date>(UtilityService.DateTimeUtc(), {

          // Set the form control as non-nullable.
          nonNullable: true
        })
      })
    });

    // Set the original administrator password into the instance.
    this.originalPassword = this.administrator.password;
  }

  /**
   * @description This method converts the form to its model variant.
   * @memberof PeoApiCredentialsAdminDialogComponent
   * @method
   * @name PeoApiCredentialsAdminDialogComponent#formToModel()
   * @private
   * @returns {PeoApiCredentialModel}
   */
  private formToModel(): AdministratorApiModel {

    // Define our model instance from the form and return it.
    return {
      created: this.form.controls.options.controls.created.value,
      emailAddress: this.form.controls.authentication.controls.emailAddress.value,
      firstName: this.form.controls.contact.controls.firstName.value,
      lastName: this.form.controls.contact.controls.lastName.value,
      locked: this.form.controls.options.controls.locked.value,
      online: this.administrator.online,
      password: this.form.controls.authentication.controls.password.value,
      passwordConfirmation: this.form.controls.authentication.controls.password.value,
      passwordExpires: this.form.controls.options.controls.passwordExpires.value,
      passwordLastChanged: this.form.controls.options.controls.passwordLastChanged.value,
      passwordPromptForChange: this.form.controls.options.controls.passwordPromptForChange.value,
      updated: this.form.controls.options.controls.updated.value
    } as AdministratorApiModel;
  }

  /**
   * @description This method closes the administrator settings form dialog.
   * @memberof SettingsAdminDialogComponent
   * @method
   * @name SettingsAdminDialogComponent#close()
   * @protected
   * @returns {void}
   */
  protected close(): void
  {
    // Close the dialog.
    this.reference.close();
  }

  /**
   * @description This method resets the administrator settings form to the original values.
   * @memberof SettingsAdminDialogComponent
   * @method
   * @name SettingsAdminDialogComponent#reset()
   * @protected
   * @returns {void}
   */
  protected reset(): void {

    // Disable the form if it is not already disabled.
    if (!this.formDisabled) {

      // Disable the form.
      this.formDisabled = true;
    }

    // Set the administrator information into the form.
    this.form.patchValue({
      authentication: {
        emailAddress: this.administrator.emailAddress,
        password: null,
        passwordConfirmation: null
      },
      contact: {
        firstName: this.administrator.firstName,
        lastName: this.administrator.lastName
      },
      options: {
        created: this.administrator.created,
        locked: this.administrator.locked,
        passwordExpires: this.administrator.passwordExpires,
        passwordLastChanged: this.administrator.passwordLastChanged,
        passwordPromptForChange: this.administrator.passwordPromptForChange,
        updated: this.administrator.updated
      }
    });

    // Re-enable the form if it was disabled.
    this.formDisabled = false;
  }

  /**
   * @description This method saves the administrator settings.
   * @memberof SettingsAdminDialogComponent
   * @method
   * @name SettingsAdminDialogComponent#save()
   * @protected
   * @returns {void}
   */
  protected save(): void {

    // Localize the administrator model.
    const administrator: AdministratorApiModel = this.formToModel();

    // Ensure we have a last changed timestamp for the password.
    if (this.originalPassword !== administrator.password) {

      // Set the password last changed timestamp to the current date and time.
      administrator.passwordLastChanged = UtilityService.DateTimeUtc();
    }

    // Commit the administrator to persistent storage.
    this.service.save(administrator).subscribe({

      // Handle the error case.
      error: (e: Error) => {

        // Notify the user of the error.
        this.snackbar.open(`An error occurred while saving Administrator ${administrator.firstName} ${administrator.lastName} with:  ${e.message}`, 'Dismiss', {
          duration: 5000,
          horizontalPosition: 'end',
          panelClass: ['snackbar-error'],
          verticalPosition: 'bottom'
        });

        // Re-enable the form.
        this.formDisabled = false;
      },

      // Handle the success case.
      next: (a: AdministratorApiModel) => {

        // Notify the user of the success.
        this.snackbar.open(`Administrator ${a.firstName} ${a.lastName} saved successfully.`, 'Dismiss', {
          duration: 5000,
          horizontalPosition: 'end',
          panelClass: ['snackbar-success'],
          verticalPosition: 'bottom'
        });

        // We're done, close the dialog.
        this.reference.close(a);
      },
    });
  }

  /**
   * @description This method gets executed after the view has been initialized.
   * @memberof SettingsAdminDialogComponent
   * @method
   * @name SettingsAdminDialogComponent#ngAfterViewInit()
   * @public
   * @returns {void}
   */
  public ngAfterViewInit(): void {

    // Reset the administrator settings form to the original values.
    this.reset();
  }
}
